import React, { useEffect, useState } from 'react';
import {
  Message,
  Segment,
  Input,
  Form,
  Table,
  Popup,
  Button,
  Loader,
  Icon,
} from 'semantic-ui-react';
import { useIntl } from 'react-intl';

import additionalMessages from '../../messages';
import { crudRead } from '../../actions';
import { store } from '../../store';
import {
  savePermissions,
  getProject,
  updatePermissions,
  deletePermissions,
  getWithAuth,
} from '../../api';
import { batch, useSelector } from 'react-redux';
import {
  setProjectModified,
  uiConfirmAction,
} from '../mainframe/mainframe.reducer';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const PermissionsPanel = ({ projectId = null, isAdmin = false }) => {
  const { formatMessage } = useIntl();
  const { email } = useSelector((state) => state.mainFrame);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);

  const permissionOptions = [
    {
      key: 'read',
      value: 1,
      text: formatMessage(additionalMessages.read),
    },
    {
      key: 'readwrite',
      value: 2,
      text: formatMessage(additionalMessages.readwrite),
    },
    {
      key: 'admin',
      value: 3,
      text: formatMessage(additionalMessages.admin),
    },
  ];

  const permissionStrings = {
    1: 'read',
    2: 'readwrite',
    3: 'admin',
  };

  const queryKey = [projectId, 'permissions'];
  const {
    data: projectPermissions,
    isFetching,
    error: requestError,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      return await getWithAuth(`project/${projectId}/permissions/all`);
    },
    initialData: null,
    retry: false,
    enabled: true,
  });
  const qc = useQueryClient();

  const handlePermissionChange = (o) => {
    const { userId, value, email } = o;
    setErrorMessage(null);
    updatePermissions(
      { projectId },
      { user_id: userId, permission: value, email }
    )
      .then(() => {
        batch(() => {
          store.dispatch(crudRead({ api: getProject, projectId }));
          store.dispatch(setProjectModified(true));
        });
        qc.invalidateQueries({ queryKey });
      })
      .catch((err) => {
        console.error(err);
        setErrorMessage(err.detail);
      });
  };

  const addUser = (e) => {
    e.preventDefault();
    setErrorMessage(null);
    // read permission === 1
    savePermissions(
      { projectId },
      { email: newUserEmail.trim(), permission: 1 }
    )
      .then(() => {
        setNewUserEmail('');
        batch(() => {
          store.dispatch(crudRead({ api: getProject, projectId }));
          store.dispatch(setProjectModified(true));
        });
        qc.invalidateQueries({ queryKey });
      })
      .catch((err) => {
        console.error(err);
        setErrorMessage(err.detail);
      });
  };

  const removeUser = (userId, email, userName) => {
    return () => {
      store.dispatch(
        uiConfirmAction({
          header: formatMessage(additionalMessages.deleteUser),
          message: formatMessage(additionalMessages.confirm),
          size: 'mini',
          onConfirm: () => {
            setErrorMessage(null);
            deletePermissions({ projectId }, { user_id: userId, email })
              .then(() => {
                batch(() => {
                  store.dispatch(crudRead({ api: getProject, projectId }));
                  store.dispatch(setProjectModified(true));
                });
                qc.invalidateQueries({ queryKey });
              })
              .catch((err) => {
                console.error(err);
                setErrorMessage(err);
              });
          },
        })
      );
    };
  };

  if (isFetching) {
    return <Loader active inline />;
  }

  let userRows = [];
  for (const { user, permission } of projectPermissions) {
    const isCurrentUser = user.email === email;
    let permissionCell = formatMessage(
      additionalMessages[permissionStrings[permission]]
    );
    // Current user can't drop their own permissions on the project
    if (isAdmin && !isCurrentUser) {
      permissionCell = (
        <Form.Dropdown
          userId={user.id}
          options={permissionOptions}
          defaultValue={permission}
          onChange={(_, data) =>
            handlePermissionChange({
              userId: data.userId,
              value: data.value,
              email: user.email,
            })
          }
        />
      );
    }

    userRows.push(
      <Table.Row key={user.id}>
        <Table.Cell data-testid="userName">
          {user.id == null ? (
            <>{user.email} (Pending)</>
          ) : (
            <span>
              {user.first_name} {user.last_name} &lt;{user.email}&gt;
            </span>
          )}
        </Table.Cell>
        <Table.Cell collapsing data-testid="permissionCell">
          {permissionCell}
        </Table.Cell>
        <Table.Cell collapsing data-testid="trashIcon">
          {isAdmin ? (
            <Button
              basic
              icon="trash alternate"
              title={formatMessage(additionalMessages.delete)}
              onClick={removeUser(user.id, user.email)}
              disabled={isCurrentUser}
            />
          ) : null}
        </Table.Cell>
      </Table.Row>
    );
  }

  return (
    <Segment basic>
      <Table unstackable compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell data-testid="user">
              {formatMessage(additionalMessages.user)}
            </Table.HeaderCell>
            <Table.HeaderCell colSpan={2} data-testid="permissions">
              {formatMessage(additionalMessages.permissions)}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>{userRows}</Table.Body>
      </Table>

      {isAdmin ? (
        <div className="ui equal width form">
          <div className="add-permission-ctrls">
            <Form.Field
              name="newUserEmail"
              control={Input}
              placeholder={formatMessage(additionalMessages.userEmail)}
              onChange={(_, { value }) => {
                setNewUserEmail(value);
                if (!value || !email) {
                  return false;
                } else if (value === email) {
                  setErrorMessage(
                    formatMessage(additionalMessages.userAlreadyExists)
                  );
                } else {
                  setErrorMessage(null);
                }
              }}
              value={newUserEmail}
              data-testid="newUserEmail"
              style={{ flex: 1 }}
            />
            <Button
              onClick={addUser}
              icon="user plus"
              content={formatMessage(additionalMessages.addUser)}
              disabled={newUserEmail.indexOf('@') < 0 || newUserEmail === email}
              title={formatMessage(additionalMessages.addUser)}
              data-testid="submitNewUser"
            />
          </div>
          <Message
            error
            visible={errorMessage != null}
            onDismiss={() => {
              setErrorMessage(null);
            }}
            header={formatMessage(additionalMessages.addUserErrorHeader)}
            content={
              errorMessage ||
              formatMessage(additionalMessages.addUserErrorContent)
            }
          />
          <Message info visible compact>
            <div>
              <li>
                <b>Read Only</b> users can only see the saved state of the
                project, so any in-work changes are not visible to them.
                <ul>
                  <li>
                    After adding a user, you must Projects -&gt; Save
                    <Icon
                      name="save"
                      color="red"
                      style={{ marginLeft: '0.25rem' }}
                    />
                    before a <b>Read Only</b> user can see the project updates.
                  </li>
                </ul>
              </li>
              <li>
                <b>Read/Write</b> and <b>Admin</b> users can see the current
                version of the project immediately after being added. They will
                also see real-time updates to the project if it is currently
                open.
              </li>
              <li>
                <b>Admin</b> users can also add/remove users and change
                permissions.
              </li>
            </div>
          </Message>
        </div>
      ) : null}
    </Segment>
  );
};

export default PermissionsPanel;
